import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import withLayout from '../../../../../core/components/with-layout';
import { CREATE_CONSENT_FORM } from '../../../../../core/graphql/consent-forms';
import {
  BaseContainer, TopContainer, Link, ContentContainer,
  Title, HeaderRow, TopicTitle, NameInputRow, TopicDescription,
  NameInput,
  UploadConsentFormRow,
  Italics, UploadButton, UploadButtonContainer, UploadErrorHandler,
  UploadErrorContainer,
  ButtonsRow, SubmitButton, CancelButton,
} from './styles';

import {
  Loader,
  ContainerLoading,
} from '../../styles';
import UploadCompletedMessage from './upload-complete-message';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const SUPPORTED_FORMATS = ['application/pdf'];

const CreateConsentForm = ({ companyId }) => {
  const history = useHistory();
  const [name, setName] = useState();
  const [uploadedFileData, setUploadedFileData] = useState({});
  const [uploadError, setUploadError] = useState('');
  const [nameError, setNameError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isUploadDone, setIsUploadDone] = useState(true);

  const [
    createConsentForm,
    {
      loading: isConsentFormCreating,
    },
  ] = useMutation(
    CREATE_CONSENT_FORM,
  );

  const redirectToTemplateLibrary = () => {
    history.push('/template-library#Consent');
  };

  const {
    fileKey,
    fileUrl,
  } = uploadedFileData;

  const handleFileUploadCompleted = (url, key) => {
    setUploadedFileData({
      fileUrl: url,
      fileKey: key,
    });
  };

  const handleCreateConsentForm = async () => {
    if (!name?.length) {
      setNameError('Please enter a name for your consent form');
      return;
    }

    if (!fileKey || !fileUrl) {
      setUploadError('Please upload a consent form and click "Save"');
      return;
    }

    try {
      await createConsentForm({
        variables: {
          name,
          fileKey,
          companyId,
        },
      });
      redirectToTemplateLibrary();
    } catch (ex) {
      setSubmitError(ex);
    }
  };

  return (
    <BaseContainer>
      {
        (
          isConsentFormCreating
        ) && (
          <ContainerLoading>
            <Loader active data-loader="create-consent-form-loader" />
          </ContainerLoading>
        )
      }
      <TopContainer>
        <Link onClick={redirectToTemplateLibrary}>
          {'<'}
          {' '}
          Return to Template Library
        </Link>
      </TopContainer>
      <ContentContainer>
        <HeaderRow>
          <Title>Upload a consent form</Title>
        </HeaderRow>
        <NameInputRow>
          <TopicTitle>
            Consent form name
          </TopicTitle>
          <TopicDescription>
            The consent form name will not be visible to participants.
          </TopicDescription>
          <NameInput
            placeholder="Enter a name"
            onChange={(e) => setName(e.target.value)}
            value={name}
          />
          {
            nameError
            && (
              <UploadErrorContainer>
                <UploadErrorHandler
                  error={{ message: nameError }}
                  defaultMessage={nameError}
                />
              </UploadErrorContainer>
            )
          }
        </NameInputRow>
        <UploadConsentFormRow>
          <TopicTitle>
            Upload consent form
          </TopicTitle>
          <TopicDescription>
            <Italics>
              .pdf only
            </Italics>
          </TopicDescription>
          <UploadButtonContainer>
            <UploadButton
              text="Upload"
              index={1}
              onSetUploadError={setUploadError}
              endpointUrl="/presigned_url_upload_pdf"
              onChangeMedia={handleFileUploadCompleted}
              mediaUrl={fileUrl}
              mediaKey={fileKey}
              maxFileSize={MAX_FILE_SIZE}
              supportedFormats={SUPPORTED_FORMATS}
              maxHeight="32px"
              uploadCompletedFc={UploadCompletedMessage}
              setIsUploadDone={setIsUploadDone}
            />
            {
              uploadError
              && (
                <UploadErrorContainer>
                  <UploadErrorHandler
                    revealError
                    error={{ message: uploadError }}
                    defaultMessage="Error creating consent form"
                  />
                </UploadErrorContainer>
              )
            }
          </UploadButtonContainer>
        </UploadConsentFormRow>
        <ButtonsRow>
          <CancelButton onClick={redirectToTemplateLibrary}>Cancel</CancelButton>
          <SubmitButton onClick={handleCreateConsentForm} disabled={!isUploadDone}>Save</SubmitButton>
        </ButtonsRow>
      </ContentContainer>
      {
        submitError
        && (
          <UploadErrorContainer>
            <UploadErrorHandler
              error={submitError}
              defaultMessage="Error creating consent form"
            />
          </UploadErrorContainer>
        )
      }
    </BaseContainer>
  );
};

CreateConsentForm.propTypes = {
  companyId: PropTypes.string.isRequired,
};

const mapStateToProps = ({ authentication }) => ({
  companyId: authentication.company?.id,
});

const enhance = compose(
  withLayout({ selectedTab: null }),
  connect(mapStateToProps, null),
);

export default enhance(CreateConsentForm);
